import { useTranslation } from 'react-i18next';

import { UserAvatar } from '../photo/UserAvatar';

/**
 * One person in a dropdown list — a search hit or a recent visit. Both lists
 * live in the same panel, so they share a row rather than drifting apart.
 *
 * The row is two targets: the wide button, which navigates in place, and the
 * optional `href` cell, which opens the person in a new tab.
 */
export function UserResultRow({
  oid,
  displayName,
  userPrincipalName,
  href,
  active = false,
  onSelect,
  onOpenInNewTab,
  onMouseEnter,
}: {
  oid: string;
  displayName: string;
  userPrincipalName: string;
  /** Omit where picking only fills a field — there is nothing to open. */
  href?: string;
  /** Holds the keyboard cursor: tints the row and pins the open-in-tab cell. */
  active?: boolean;
  onSelect: () => void;
  /** The dropdown deliberately stays up; use this to record the visit. */
  onOpenInNewTab?: () => void;
  onMouseEnter?: () => void;
}) {
  const { t } = useTranslation();
  const label = t('topSearch.openInNewTab', 'Ouvrir dans un nouvel onglet');

  return (
    <li
      className={active ? 'result-row is-active' : 'result-row'}
      onMouseEnter={onMouseEnter}
    >
      <button type="button" onClick={onSelect} className="result-row-pick">
        <UserAvatar
          userId={oid}
          displayName={displayName || userPrincipalName}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {displayName || userPrincipalName}
          </p>
          <p className="mono truncate text-xs text-muted">
            {userPrincipalName}
          </p>
        </div>
      </button>
      {href !== undefined && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={onOpenInNewTab}
          title={label}
          aria-label={label}
          className="result-row-open"
        >
          <svg
            className="size-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      )}
    </li>
  );
}
